/**
 * Generate Jig stage-assembly module.
 * Combines built model data with generated stage instructions to produce the final
 * Jig payload shape sent back to the API.
 */
import type {
  BuiltModelData,
  StageInstructionWithTools,
  ViewpointBounds,
  ViewpointSceneContext,
  ViewpointVector3,
} from './generate-jig.types'
import { deepClone, generateShortUid } from './generate-jig.utils'
import { applyToolsToStage } from './generate-jig.stage-tools'
import { buildFallbackStageInstructions, createDefaultStage } from './generate-jig.ai-prompts'
import { computeAabbFramedDistance, computeAdaptiveMinDistance, yawPitchToDirection } from './viewpoint/viewpoint-math'
import { writeViewpointToStage } from './viewpoint/write-viewpoint-to-stage'

const FINAL_STAGE_FOV_DEG = 60
const FINAL_STAGE_ASPECT = 16 / 9
const FINAL_STAGE_FRAME_FILL = 0.9
const FINAL_STAGE_VIEW_DIRECTION = yawPitchToDirection(45, 25)

function computeTightSceneBounds(sceneContext: ViewpointSceneContext): ViewpointBounds {
  const min: ViewpointVector3 = { x: Infinity, y: Infinity, z: Infinity }
  const max: ViewpointVector3 = { x: -Infinity, y: -Infinity, z: -Infinity }

  sceneContext.elements.forEach(function (element) {
    const bounds = element.bounds
    const halfX = bounds.size.x / 2
    const halfY = bounds.size.y / 2
    const halfZ = bounds.size.z / 2

    min.x = Math.min(min.x, bounds.center.x - halfX)
    min.y = Math.min(min.y, bounds.center.y - halfY)
    min.z = Math.min(min.z, bounds.center.z - halfZ)
    max.x = Math.max(max.x, bounds.center.x + halfX)
    max.y = Math.max(max.y, bounds.center.y + halfY)
    max.z = Math.max(max.z, bounds.center.z + halfZ)
  })

  if (!isFinite(min.x)) {
    return sceneContext.sceneBounds
  }

  return {
    center: {
      x: (min.x + max.x) / 2,
      y: (min.y + max.y) / 2,
      z: (min.z + max.z) / 2,
    },
    size: {
      x: max.x - min.x,
      y: max.y - min.y,
      z: max.z - min.z,
    },
  }
}

function setFinalStageOverview(stages: any[], sceneContext: ViewpointSceneContext | null): void {
  if (!sceneContext || stages.length < 2) {
    return
  }

  const finalStage = stages[stages.length - 1]
  if (finalStage.IsViewpointActiveInStage) {
    return
  }

  const sceneBounds = computeTightSceneBounds(sceneContext)
  const minDistance = computeAdaptiveMinDistance(sceneBounds, sceneContext.globalScale)
  const distance = computeAabbFramedDistance(sceneBounds, FINAL_STAGE_VIEW_DIRECTION, minDistance, {
    fovDeg: FINAL_STAGE_FOV_DEG,
    aspect: FINAL_STAGE_ASPECT,
    frameFill: FINAL_STAGE_FRAME_FILL,
  })
  const targetPosition = sceneBounds.center
  const cameraPosition = {
    x: targetPosition.x + (FINAL_STAGE_VIEW_DIRECTION.x * distance),
    y: targetPosition.y + (FINAL_STAGE_VIEW_DIRECTION.y * distance),
    z: targetPosition.z + (FINAL_STAGE_VIEW_DIRECTION.z * distance),
  }

  writeViewpointToStage(finalStage, cameraPosition, targetPosition, distance)
}

/**
 * Clones the copied Jig template, replaces its model payload, and expands the
 * normalized stage instruction list into a full set of serialized stages.
 */
export function applyModelAndStagesToJig(
  baseJig: any,
  modelData: BuiltModelData,
  stageInstructions: StageInstructionWithTools[],
  userQuestion: string
): any {
  const jig = deepClone(baseJig)

  if (!jig.JigMetaData) {
    jig.JigMetaData = {}
  }

  const trimmedQuestion = userQuestion ? userQuestion.trim() : ''
  jig.JigMetaData.ProjectDescription = trimmedQuestion.length > 0 ? trimmedQuestion : ''

  if (!jig.JigModelData) {
    jig.JigModelData = {}
  }

  jig.JigModelData.Models = [modelData.modelEntry]
  jig.JigModelData.Meshes = modelData.meshes
  jig.JigModelData.HasTexture = modelData.hasTexture
  jig.JigModelData.Textures = deepClone(modelData.textures)

  if (!Array.isArray(jig.JigModelData.Materials)) {
    jig.JigModelData.Materials = []
  }

  if (!jig.JigStageData) {
    jig.JigStageData = { Stages: [] }
  }

  if (!Array.isArray(jig.JigStageData.Stages) || jig.JigStageData.Stages.length === 0) {
    jig.JigStageData.Stages = [createDefaultStage(modelData.serializedElements)]
  }

  const templateStage = deepClone(jig.JigStageData.Stages[0])
  // Remove SerializedElements from the template to avoid cloning them N times as part of the full stage.
  // Each stage gets its own fresh clone of the model's serializedElements instead.
  const { SerializedElements: _discarded, ...templateStageWithoutElements } = templateStage

  const normalizedStages = stageInstructions.length > 0 ? stageInstructions : buildFallbackStageInstructions(userQuestion)

  const stages = normalizedStages.map((stageData: StageInstructionWithTools, index: number) => {
    const stage = {
      ...deepClone(templateStageWithoutElements),
      SerializedElements: deepClone(modelData.serializedElements),
      StageInstructions: stageData.instruction,
      StageOrder: index,
      UID: generateShortUid(),
      StageUid: generateShortUid(),
    }

    applyToolsToStage(stage, stageData, modelData.sceneContext)

    return stage
  })

  setFinalStageOverview(stages, modelData.sceneContext)

  jig.JigStageData.Stages = stages

  return jig
}
